"use client";

import { c as _c } from "react/compiler-runtime";
import { SearchIcon } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { memo, useCallback, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTableFilter } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { agriculturalActivitylist, livestockActivitylist, projectStatusEnum, projectWorkflowStatusEnum } from "@/data/entity-types";
import { INVESTMENT_PROGRAM_OPTIONS } from "@/lib/investment-programs";
type ProjectFiltersProps = {
  uniqueHarvests: string[];
  producerClassifications: {
    id: string;
    title: string;
  }[];
  technicalProjectType?: "operating_cost" | "investment";
};
function getSearchParamArray(urlSearchParams: URLSearchParams, key: string): string[] {
  const value = urlSearchParams.getAll(key);
  if (value.length > 0) return value;
  const single = urlSearchParams.get(key);
  return single ? single.split(",").filter(Boolean) : [];
}
export const ProjectFilters = memo(function ProjectFiltersImpl(t0) {
  const $ = _c(98);
  const {
    uniqueHarvests,
    producerClassifications,
    technicalProjectType: t1
  } = t0;
  const technicalProjectType = t1 === undefined ? "operating_cost" : t1;
  const router = useRouter();
  const urlSearchParams = useSearchParams();
  const isInvestment = technicalProjectType === "investment";
  let t2;
  if ($[0] !== urlSearchParams) {
    t2 = urlSearchParams.get("search") ?? "";
    $[0] = urlSearchParams;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== urlSearchParams) {
    t3 = getSearchParamArray(urlSearchParams, "status");
    $[2] = urlSearchParams;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== urlSearchParams) {
    t4 = getSearchParamArray(urlSearchParams, "workflowStatus");
    $[4] = urlSearchParams;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== urlSearchParams) {
    t5 = getSearchParamArray(urlSearchParams, "activity");
    $[6] = urlSearchParams;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== urlSearchParams) {
    t6 = getSearchParamArray(urlSearchParams, "harvest");
    $[8] = urlSearchParams;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== urlSearchParams) {
    t7 = getSearchParamArray(urlSearchParams, "classification");
    $[10] = urlSearchParams;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== urlSearchParams) {
    t8 = getSearchParamArray(urlSearchParams, "program");
    $[12] = urlSearchParams;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== urlSearchParams) {
    t9 = urlSearchParams.get("updatedFrom") ?? "";
    $[14] = urlSearchParams;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== urlSearchParams) {
    t10 = urlSearchParams.get("updatedTo") ?? "";
    $[16] = urlSearchParams;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t11 = {
      search: t2,
      status: t3,
      workflowStatus: t4,
      activity: t5,
      harvest: t6,
      classification: t7,
      program: t8,
      updatedFrom: t9,
      updatedTo: t10
    };
    $[18] = t10;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const searchParams = t11;
  const [globalFilter, setGlobalFilter] = useState(searchParams.search);
  let t12;
  if ($[28] !== router || $[29] !== urlSearchParams) {
    t12 = updates => {
      const next = new URLSearchParams(urlSearchParams.toString());
      for (const [key, value] of Object.entries(updates)) {
        next.delete(key);
        if (Array.isArray(value)) {
          for (const v of value) {
            next.append(key, v);
          }
        } else {
          if (value !== null && value !== undefined && value !== "") {
            next.set(key, String(value));
          }
        }
      }
      router.push(`?${next.toString()}`);
    };
    $[28] = router;
    $[29] = urlSearchParams;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const updateFilters = t12;
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [...agriculturalActivitylist.map(_temp), ...livestockActivitylist.map(_temp2)];
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const allActivities = t13;
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = Object.entries(projectStatusEnum).map(_temp3);
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const statusOptions = t14;
  let t15;
  if ($[33] !== producerClassifications) {
    t15 = producerClassifications.map(_temp4);
    $[33] = producerClassifications;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const classificationOptions = t15;
  let t16;
  if ($[35] !== uniqueHarvests) {
    t16 = uniqueHarvests.map(_temp5);
    $[35] = uniqueHarvests;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const harvestOptions = t16;
  let t17;
  if ($[37] !== updateFilters) {
    t17 = e => {
      const value_1 = e.target.value;
      setGlobalFilter(value_1);
      updateFilters({
        search: value_1
      });
    };
    $[37] = updateFilters;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const handleGlobalFilterChange = t17;
  let t18;
  if ($[39] !== updateFilters) {
    t18 = values => {
      updateFilters({
        status: values
      });
    };
    $[39] = updateFilters;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  const handleStatusChange = t18;
  let t19;
  if ($[41] !== updateFilters) {
    t19 = values_0 => {
      updateFilters({
        activity: values_0
      });
    };
    $[41] = updateFilters;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  const handleActivityChange = t19;
  let t20;
  if ($[43] !== updateFilters) {
    t20 = values_1 => {
      updateFilters({
        harvest: values_1
      });
    };
    $[43] = updateFilters;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const handleHarvestChange = t20;
  let t21;
  if ($[45] !== updateFilters) {
    t21 = values_2 => {
      updateFilters({
        classification: values_2
      });
    };
    $[45] = updateFilters;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const handleClassificationChange = t21;
  let t22;
  if ($[47] !== updateFilters) {
    t22 = values_3 => {
      updateFilters({
        program: values_3
      });
    };
    $[47] = updateFilters;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  const handleProgramChange = t22;
  let t23;
  if ($[49] !== updateFilters) {
    t23 = e_0 => {
      updateFilters({
        updatedFrom: e_0.target.value
      });
    };
    $[49] = updateFilters;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  const handleUpdatedFromChange = t23;
  let t24;
  if ($[51] !== updateFilters) {
    t24 = e_1 => {
      updateFilters({
        updatedTo: e_1.target.value
      });
    };
    $[51] = updateFilters;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  const handleUpdatedToChange = t24;
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = Object.entries(projectWorkflowStatusEnum).map(_temp6);
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  const workflowStatusOptions = t25;
  let t26;
  if ($[54] !== updateFilters) {
    t26 = values_4 => {
      updateFilters({
        workflowStatus: values_4
      });
    };
    $[54] = updateFilters;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  const handleWorkflowStatusChange = t26;
  let t27;
  if ($[56] !== updateFilters) {
    t27 = () => {
      setGlobalFilter("");
      updateFilters({
        search: "",
        status: [],
        workflowStatus: [],
        activity: [],
        harvest: [],
        classification: [],
        program: [],
        updatedFrom: "",
        updatedTo: ""
      });
    };
    $[56] = updateFilters;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  const clearFilters = t27;
  const hasActiveFilters = searchParams.search || searchParams.status.length > 0 || searchParams.workflowStatus.length > 0 || searchParams.activity.length > 0 || searchParams.harvest.length > 0 || searchParams.classification.length > 0 || searchParams.program.length > 0 || searchParams.updatedFrom || searchParams.updatedTo;
  const t28 = isInvestment ? "Buscar por c\xF3digo, produtor, CPF/CNPJ ou programa..." : "Buscar por c\xF3digo, atividade, safra ou banco...";
  let t29;
  if ($[58] !== globalFilter || $[59] !== handleGlobalFilterChange || $[60] !== t28) {
    t29 = <div className="min-w-56 flex-1 sm:max-w-sm"><Input aria-label="Buscar projetos" leftIcon={SearchIcon} onChange={handleGlobalFilterChange} placeholder={t28} value={globalFilter} /></div>;
    $[58] = globalFilter;
    $[59] = handleGlobalFilterChange;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== handleStatusChange || $[63] !== searchParams.status) {
    t30 = <DataTableFilter onChange={handleStatusChange} options={statusOptions} title="Status" values={searchParams.status} />;
    $[62] = handleStatusChange;
    $[63] = searchParams.status;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== handleWorkflowStatusChange || $[66] !== searchParams.workflowStatus) {
    t31 = <DataTableFilter onChange={handleWorkflowStatusChange} options={workflowStatusOptions} title="Etapa" values={searchParams.workflowStatus} />;
    $[65] = handleWorkflowStatusChange;
    $[66] = searchParams.workflowStatus;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== handleActivityChange || $[69] !== handleHarvestChange || $[70] !== handleProgramChange || $[71] !== harvestOptions || $[72] !== isInvestment || $[73] !== searchParams.activity || $[74] !== searchParams.harvest || $[75] !== searchParams.program) {
    t32 = isInvestment ? <DataTableFilter onChange={handleProgramChange} options={INVESTMENT_PROGRAM_OPTIONS} title="Programa" values={searchParams.program} /> : <><DataTableFilter onChange={handleActivityChange} options={allActivities} title="Atividade" values={searchParams.activity} /><DataTableFilter onChange={handleHarvestChange} options={harvestOptions} title="Safra" values={searchParams.harvest} /></>;
    $[68] = handleActivityChange;
    $[69] = handleHarvestChange;
    $[70] = handleProgramChange;
    $[71] = harvestOptions;
    $[72] = isInvestment;
    $[73] = searchParams.activity;
    $[74] = searchParams.harvest;
    $[75] = searchParams.program;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== classificationOptions || $[78] !== handleClassificationChange || $[79] !== searchParams.classification) {
    t33 = <DataTableFilter onChange={handleClassificationChange} options={classificationOptions} title="Enquadramento" values={searchParams.classification} />;
    $[77] = classificationOptions;
    $[78] = handleClassificationChange;
    $[79] = searchParams.classification;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] !== handleUpdatedFromChange || $[82] !== handleUpdatedToChange || $[83] !== isInvestment || $[84] !== searchParams.updatedFrom || $[85] !== searchParams.updatedTo) {
    t34 = isInvestment ? <><div className="min-w-36"><Input id="updatedFrom" label="Atualizado de" onChange={handleUpdatedFromChange} type="date" value={searchParams.updatedFrom} /></div><div className="min-w-36"><Input id="updatedTo" label={"Atualizado at\xE9"} onChange={handleUpdatedToChange} type="date" value={searchParams.updatedTo} /></div></> : null;
    $[81] = handleUpdatedFromChange;
    $[82] = handleUpdatedToChange;
    $[83] = isInvestment;
    $[84] = searchParams.updatedFrom;
    $[85] = searchParams.updatedTo;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  let t35;
  if ($[87] !== clearFilters || $[88] !== hasActiveFilters) {
    t35 = hasActiveFilters ? <Button className="text-muted-foreground" onClick={clearFilters} variant="ghost">Limpar</Button> : null;
    $[87] = clearFilters;
    $[88] = hasActiveFilters;
    $[89] = t35;
  } else {
    t35 = $[89];
  }
  let t36;
  if ($[90] !== t29 || $[91] !== t30 || $[92] !== t31 || $[93] !== t32 || $[94] !== t33 || $[95] !== t34 || $[96] !== t35) {
    t36 = <><div className="flex flex-wrap items-end gap-2">{t29}{t30}{t31}{t32}{t33}{t34}{t35}</div></>;
    $[90] = t29;
    $[91] = t30;
    $[92] = t31;
    $[93] = t32;
    $[94] = t33;
    $[95] = t34;
    $[96] = t35;
    $[97] = t36;
  } else {
    t36 = $[97];
  }
  return t36;
});
function _temp(activity) {
  return {
    label: activity.title,
    value: activity.id
  };
}
function _temp2(activity_0) {
  return {
    label: activity_0.title,
    value: activity_0.id
  };
}
function _temp3(t0) {
  const [value_0, label] = t0;
  return {
    label,
    value: value_0
  };
}
function _temp4(classification) {
  return {
    label: classification.title,
    value: classification.title
  };
}
function _temp5(harvest) {
  return {
    label: harvest,
    value: harvest
  };
}
function _temp6(t0) {
  const [value_2, label_0] = t0;
  return {
    label: label_0,
    value: value_2
  };
}